import {
  Box,
  Button,
  Container,
  Flex,
  Graphic,
  Heading,
  IconButton,
  LinkButton,
  Stack,
  Text,
  useTheme,
  useToast,
} from '@thirstycamel/ui'
import { useRouter } from 'next/router'
import React, { memo, useEffect, useState } from 'react'
import TrendingKeywords from '../../components/TrendingKeywords'
import { useUser } from '../../hooks/useUser'
import { useActions, useStore } from '../../store/hooks'
import { LayoutMain, LayoutMainProps } from '../LayoutMain'
import { useBirthdayGameStatus } from '../../hooks/useGamesStatus'
import { differenceInDays } from 'date-fns'

export enum LoyaltyPhase {
  NEW_MEMBER = 'new_member',
  POTENTIAL = 'potential',
  WIN_BACK = 'win_back',
  AT_RISK = 'at_risk',
  ENGAGED = 'engaged',
}

export const getSidebarItems = user => [
  {
    href: '/account/offers',
    text: `My offers${user?.activeOffersCount ? ` (${user?.activeOffersCount})` : ''}`,
  },
  {
    href: '/account/offers/redeemed',
    text: `Redeemed Offers`,
  },
  { href: '/account', text: 'Account information' },
  { href: '/account/addresses', text: 'Address book' },
  { href: '/account/orders', text: 'My orders' },
  { href: '/account/reviews', text: 'My product reviews' },
  { href: '/account/products', text: 'Favourite products' },
  { href: '/account/email-preferences', text: 'Email preferences' },
  { href: '/account/self-exclusion', text: 'Self exclusion' },
]

export interface LayoutAccountProps extends LayoutMainProps {}

export function LayoutAccount({ children, ...restProps }: LayoutAccountProps) {
  const router = useRouter()
  const theme = useTheme()

  const [isSidebarVisible, setSidebarVisible] = useState(false)

  const { logout, getUser } = useActions(actions => actions.auth)
  const { showModal } = useActions(actions => actions.modal)
  const { isFetching } = useStore(store => store.auth)
  const { user, isLoggedIn, isBirthdayToday } = useUser()
  const birthdayGame = useBirthdayGameStatus()

  const toast = useToast()

  useEffect(() => {
    getUser()
  }, [])

  useEffect(() => {
    if (!isLoggedIn) {
      router.push('/login')
    }
  }, [isLoggedIn])

  const sidebarItems = getSidebarItems(user)

  const [daysSinceJoining, setDaysSinceJoining] = useState(1)

  const calculateDaysSinceJoining = (date?: Date | string | null): number => {
    if (!date) return 1
    const d = typeof date === 'string' ? new Date(date) : date
    if (Number.isNaN(d.getTime())) return 1
    const days = Math.abs(differenceInDays(new Date(), d))
    return days > 0 ? days : 1
  }

  const [camels, setCamels] = useState(1)
  const calculateCamels = (days: number) => {
    const LITRES_PER_3_MIN = 30
    const litres = days
    if (litres === 1) {
      return 1
    }

    setCamels(Math.ceil(litres / LITRES_PER_3_MIN))
  }

  useEffect(() => {
    if (user) {
      const joinDate =
        user.createdAt != null
          ? user.createdAt
          : user.accountActivatedDate != null
          ? user.accountActivatedDate
          : null
      const days = calculateDaysSinceJoining(joinDate)
      setDaysSinceJoining(days)
      calculateCamels(days)
    }
  }, [user])

  return (
    <LayoutMain {...restProps} noContainer>
      <Flex>
        <Flex display={['flex', , 'none']} flexShrink={0} bg="pink.500" justify="center" p={2}>
          <IconButton
            icon="menu"
            isRound
            fontSize="2xl"
            variantColor="pink"
            onClick={() => setSidebarVisible(state => !state)}
          />
        </Flex>

        <Flex align="center" bg="pink.500" py={[6, 6]} color="white" w="100%">
          <Container
            display="flex"
            justifyContent="center"
            alignItems="center"
            size="lg"
            px={[5, 10, 6, 16]}
          >
            <Graphic
              name="humpClubLogo"
              size={['128px', '160px', '192px']}
              transform="rotate(-10deg)"
              mr={6}
              flexShrink={[1, 0]}
              my={-8}
              pos="relative"
              zIndex={2}
              display={['none', , 'block']}
            />

            <Flex
              direction={['column', , 'row']}
              alignItems="flex-start"
              flexShrink={1}
              flexGrow={1}
            >
              <Flex justify="space-between" align="center" flexGrow={1} pr={6} mb={[3, , 0]}>
                <Box>
                  <Heading textTransform="uppercase" fontSize={['2xl', '3xl']}>
                    Hump Club
                  </Heading>
                  <Text textTransform="uppercase" fontSize="sm" fontFamily="heading">
                    HEY, {user?.firstName}!
                  </Text>
                  <Text>
                    {`You’ve been a Hump Club member for
                    ${
                      daysSinceJoining > 1 ? `${daysSinceJoining} days` : `${daysSinceJoining} day`
                    }. That’s almost as many days as litres ${camels} camel${
                      camels > 1 ? 's' : ''
                    } can drink in 3 minutes!`}
                  </Text>
                  {!birthdayGame?.isFetching && birthdayGame?.slug && isBirthdayToday && (
                    <Box paddingTop={6}>
                      <Text textTransform="uppercase" fontSize="sm" fontFamily="heading">
                        Happy birthday! Don't miss out on your special birthday game!
                      </Text>
                      <LinkButton
                        href={`/games/${birthdayGame?.slug}`}
                        variant="outline"
                        variantColor="pink"
                        flexShrink={0}
                      >
                        Play now
                      </LinkButton>
                    </Box>
                  )}
                </Box>
              </Flex>

              <Stack display="flex" height="100%" flexGrow={1} spacing={4}>
                <LinkButton
                  href="/account/licence"
                  variant="outline"
                  variantColor="pink"
                  flexShrink={0}
                >
                  Your member card
                </LinkButton>
              </Stack>
            </Flex>
          </Container>
        </Flex>
      </Flex>

      <Container display="flex" flexGrow={1} size="lg" px={0} pos="relative">
        <Flex
          direction="column"
          align="flex-start"
          flexGrow={1}
          w={['100vw', , '100%']}
          maxWidth={[, , '13rem', '15.7rem']}
          px={[10, , 8]}
          py={12}
          bg="gray.200"
          borderRight="1px"
          borderColor="gray.200"
          position={['fixed', , 'relative']}
          right={['100%', , 'auto']}
          top={0}
          bottom={0}
          transform={isSidebarVisible ? 'translateX(100%)' : null}
          zIndex={[theme.zIndices.overlay, , 0]}
          transition="200ms ease transform"
          overflowY={['auto', 'initial']}
        >
          <IconButton
            icon={isSidebarVisible ? 'close' : 'menu'}
            pos="absolute"
            right={3}
            top={3}
            zIndex={2}
            isRound
            fontSize={isSidebarVisible ? 'lg' : '2xl'}
            variantColor="pink"
            display={['flex', , 'none']}
            onClick={() => setSidebarVisible(state => !state)}
          />

          <Box position="absolute" right={0} top={0} bottom={0} w="100vw" bg="gray.50" />

          <Stack
            w="100%"
            mb={8}
            opacity={[isSidebarVisible ? 1 : 0, , 1]}
            transition="100ms ease opacity"
          >
            {sidebarItems.map(item => (
              <LinkButton
                key={item.href}
                variant="ghost"
                href={item.href}
                variantColor={router.route === item.href ? 'black' : 'pink'}
                textAlign="left"
                justifyContent="flex-start"
                w="100%"
                px={1}
                onClick={() => setSidebarVisible(false)}
              >
                {item.text}
              </LinkButton>
            ))}
          </Stack>

          <Button
            isLoading={isFetching}
            onClick={() => logout()}
            variant="outline"
            variantColor="pink"
          >
            Log out
          </Button>
        </Flex>

        <Box bg="white" p={[6, 8]} pb={12} width="100%">
          {children}
        </Box>
      </Container>

      <TrendingKeywords />
    </LayoutMain>
  )
}

export default memo(LayoutAccount)
